import React, { useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import BackButton from '../custom/BackButton';
import { formatDatetime } from '../../utils';
import { useAppContext } from '../../context';
import { IChangeLog } from '../../globals/interfaces';
import Item from './Item';
import Loading from '../misc/Loading';
import _ from 'lodash';
import useAppData from '../../customHooks/useAppData';

export type Types = 'ADDED' | 'CHANGED' | 'FIXED';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
}

export default function ViewChangeLog({ modal, setModal }: Props) {
    const history = useHistory();
    const { changeLogData } = useAppData();

    const toggle = () => setModal(!modal);

    return (
        <CustomModal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>Change Log</h4>
                </HeaderContainer>
            </ModalHeader>
            <CustomModalBody>
                <Body changeLogData={changeLogData} history={history} />
            </CustomModalBody>
        </CustomModal>
    );
}

interface BodyProps {
    changeLogData: Array<IChangeLog>;
    history: History;
}

const Body = ({ changeLogData, history }: BodyProps) => {
    const dataMap = useMemo(() => {
        const map: Record<
            string,
            {
                added: Array<IChangeLog>;
                changed: Array<IChangeLog>;
                fixed: Array<IChangeLog>;
            }
        > = {};
        for (const item of changeLogData) {
            const { type, date } = item;
            const useDate = moment(date).format('MM/DD/YYYY');
            if (!map[useDate]) {
                map[useDate] = { added: [], changed: [], fixed: [] };
            }
            if (type === 'ADDED') {
                map[useDate].added.push(item);
            } else if (type === 'CHANGED') {
                map[useDate].changed.push(item);
            } else {
                map[useDate].fixed.push(item);
            }
        }
        return map;
    }, [changeLogData]);

    if (!changeLogData.length) {
        return <Loading />;
    }

    return (
        <Section>
            {Object.keys(dataMap).map((version) => (
                <Section key={version}>
                    <Version>{version}</Version>
                    <RenderSection
                        dataArray={dataMap[version].added}
                        type="ADDED"
                        history={history}
                    />
                    <RenderSection
                        dataArray={dataMap[version].changed}
                        type="CHANGED"
                        history={history}
                    />
                    <RenderSection
                        dataArray={dataMap[version].fixed}
                        type="FIXED"
                        history={history}
                    />
                </Section>
            ))}
        </Section>
    );
};

interface RenderSectionProps {
    dataArray: Array<IChangeLog>;
    type: Types;
    history: History;
}

const RenderSection = ({ dataArray, type, history }: RenderSectionProps) => {
    if (!dataArray.length) {
        return null;
    }

    return (
        <div>
            <Subtitle>{type}</Subtitle>
            {dataArray.map((item, i) => (
                <Item item={item} type={type} key={i} history={history} />
            ))}
        </div>
    );
};

const CustomModal = styled(Modal)`
    width: 1200px;
    max-width: 100%;
`;

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const Version = styled.h3`
    font-weight: bold;
`;

const Section = styled.div`
    margin-bottom: 25px;
    text-transform: none;
`;

const Subtitle = styled.h5`
    font-style: italic;
`;

const CustomModalBody = styled(ModalBody)`
    max-height: 600px;
    overflow-y: scroll;
`;

function sortVersions(data: Array<IChangeLog>) {
    return data.sort((a, b) => {
        const [aMajor, aMinor, aPatch] = _.get(a, [`version.split('.')`], '').map(Number);
        const [bMajor, bMinor, bPatch] = _.get(b, [`version.split('.')`], '').map(Number);

        if (aMajor !== bMajor) {
            return bMajor - aMajor;
        }
        if (aMinor !== bMinor) {
            return bMinor - aMinor;
        }
        return bPatch - aPatch;
    });
}
